import React, { useCallback, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import SearchInput from "@/components/ui/SearchInput";
import InfoTooltip from "@/components/ui/InfoTooltip";
import { WidgetTitle } from "@/components/widgets/WidgetTitle";
import { useDashboard } from "@/contexts/DashboardContext";
import { cn, formatPercentage } from "@/lib/utils";
import { TruncatedTextTooltip } from "@/components/ui/truncated-text-tooltip";
import { ChevronDown, ChevronUp, Download } from "lucide-react";
import { TablePagingFooter } from "@/components/widgets/common/TablePagingFooter";
import { fetchUserPerformanceTable, fetchUserPerformanceColumnFilter } from "@/lib/axios/api/widgets.api";
import { fetchUntilCountMatches } from "@/lib/utils/fetchUntilCountMatches";
import type { UserPerformanceTableRow } from "@/lib/axios/types/widgets.type";
import { useAgents } from "@/hooks/useAgents";
import { UserAgentDetailDialog } from "./UserAgentDetailDialog";
import { formatDuration, formatRelativeTime, renderMetricBar, getSlaColors, getTransferredPctColors } from "./tableCellHelpers";
import { useColumnFilterState, ColFilterPopover } from "./columnFilterShared";
import * as XLSX from "xlsx";
import {
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableScrollContainer,
  TableSkeletonRow,
  TableEmptyRow,
  TABLE_CARD_CLASS,
  TABLE_HEADER_TITLE_CLASS,
  type TableColumnConfig,
} from "@/components/ui/table";

// ─── Export ───────────────────────────────────────────────────────────────────

const exportToExcel = async (
  startTime: string,
  endTime: string,
  agentMicrosoftIds: string[] | null,
  queueMicrosoftIds: string[] | null,
  sortKey: string,
  sortDir: "asc" | "desc",
  setExportLoading: (v: boolean) => void
) => {
  try {
    setExportLoading(true);
    const result = await fetchUserPerformanceTable({
      startTime,
      endTime,
      agentMicrosoftIds,
      queueMicrosoftIds,
      agentNameSearchText: null,
      sortTable: sortKey ? { columnName: sortKey, columnValue: sortDir } : null,
      page: 1,
      limit: null,
      columnFilters: null,
    });
    const headers = ["User", "Total Offered", "AHT", "Transferred Rate (%)", "SLA (%)", "Last Call Taken"];
    const rows = result.data.map((r) => [
      r.userName,
      r.totalOffered,
      formatDuration(r.aht),
      r.transferredPct == null ? "—" : formatPercentage(r.transferredPct),
      r.sla == null ? "—" : formatPercentage(r.sla),
      formatRelativeTime(r.lastCallTaken),
    ]);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws["!cols"] = [{ wch: 28 }, { wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 12 }, { wch: 18 }];
    const dateStr = new Date().toISOString().split("T")[0].replace(/-/g, "");
    XLSX.utils.book_append_sheet(wb, ws, "User Performance");
    XLSX.writeFile(wb, `userperformance_${dateStr}.xlsx`);
  } catch {
    // silent
  } finally {
    setExportLoading(false);
  }
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isAbortError(e: unknown): boolean {
  const name = e instanceof Error ? e.name : (e as { name?: string } | null)?.name;
  return name === "CanceledError" || name === "AbortError";
}

function buildColumnFiltersPayload(cf: Record<string, string[]>) {
  const out = Object.entries(cf)
    .filter(([, values]) => values.length > 0)
    .map(([column, values]) => ({ column, values }));
  return out.length ? out : null;
}

// ─── Column filter hook ───────────────────────────────────────────────────────

const FILTER_PAGE_SIZE = 50;

// ─── User name cell with conditional tooltip ─────────────────────────────────

function UserNameCell({ name, onClick }: { readonly name: string; readonly onClick?: () => void }) {
  return (
    <button
      type="button"
      className="w-full cursor-pointer text-left text-primary dark:text-white"
      onClick={onClick}
    >
      <TruncatedTextTooltip content={name} className="text-left text-xs">
        {name}
      </TruncatedTextTooltip>
    </button>
  );
}

// ─── Columns config ───────────────────────────────────────────────────────────

const columns: TableColumnConfig[] = [
  { title: "User", key: "userName", width: 200, filterable: true },
  { title: "Total Offered", key: "totalOffered", width: 150, filterable: true },
  { title: "AHT", key: "aht", width: 144, filterable: true },
  { title: "Transferred Rate (%)", key: "transferredPct", width: 150, filterable: true },
  { title: "SLA (%)", key: "sla", width: 130, filterable: true },
  { title: "Last Call Taken", key: "lastCallTaken", width: 160, filterable: true },
];

// ─── Main widget ──────────────────────────────────────────────────────────────

interface UserPerformanceTableWidgetProps {
  startDate?: string;
  endDate?: string;
  agentMicrosoftIds?: string[] | null;
  queueMicrosoftIds?: string[] | null;
  // Bumped by a with-agents-and-role poll detecting a real change (e.g. an agent renamed) —
  // id lists alone don't change on a rename, so this forces a refetch even when queryKey's
  // id-based portion is unchanged (mirrors UsersTab's KPI batch effect).
  queuesDataVersion?: number;
}

const UserPerformanceTableWidget: React.FC<UserPerformanceTableWidgetProps> = ({
  startDate,
  endDate,
  agentMicrosoftIds = null,
  queueMicrosoftIds = null,
  queuesDataVersion = 0,
}) => {
  const [sortKey, setSortKey] = useState<string>("userName");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [searchText, setSearchText] = useState("");
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({});
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const { agents: agentsForDetail } = useAgents();
  const selectedAgentId = useMemo(
    () => agentsForDetail.find((a) => a.name === selectedUser)?.microsoftId ?? null,
    [agentsForDetail, selectedUser]
  );
  const { theme } = useDashboard();
  const [tableData, setTableData] = useState<UserPerformanceTableRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [exportLoading, setExportLoading] = useState(false);
  const lastQueryKeyRef = useRef("");

  const skeletonRowIds = useMemo(() => Array.from({ length: limit }, () => crypto.randomUUID()), [limit]);

  const colFilter = useColumnFilterState();

  // ── Table data fetch ──────────────────────────────────────────────────────
  React.useEffect(() => {
    if (!startDate || !endDate) {
      setTableData([]);
      lastQueryKeyRef.current = "";
      return;
    }
    const colFiltersKey = JSON.stringify(columnFilters);
    const agentKey = (agentMicrosoftIds ?? []).join(",");
    const queueKey = (queueMicrosoftIds ?? []).join(",");
    const queryKey = `${startDate}|${endDate}|${agentKey}|${queueKey}|${searchText}|${sortKey}|${sortDir}|${page}|${limit}|${colFiltersKey}|${queuesDataVersion}`;

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      if (queryKey === lastQueryKeyRef.current) return;
      lastQueryKeyRef.current = queryKey;
      setLoading(true);
      setError("");
      try {
        // TCX-6485: a brand-new agent's call data can lag the roster by up to ~60s while the
        // data source's cache warms up (Flow1/Flow2 backend timing) — the fetch that fires the
        // instant the roster/filter changes can legitimately come back empty. Retry
        // (condition-based, not a blind delay) until the row count catches up with the
        // requested agent scope.
        const result = await fetchUntilCountMatches(
          () => fetchUserPerformanceTable(
            {
              startTime: startDate,
              endTime: endDate,
              agentMicrosoftIds: agentMicrosoftIds ?? null,
              queueMicrosoftIds: queueMicrosoftIds ?? null,
              agentNameSearchText: searchText.trim() || null,
              sortTable: sortKey ? { columnName: sortKey, columnValue: sortDir } : null,
              page,
              limit,
              columnFilters: buildColumnFiltersPayload(columnFilters),
            },
            { signal: controller.signal }
          ),
          (res) => res?.total ?? res?.data?.length ?? 0,
          agentMicrosoftIds?.length,
          { signal: controller.signal }
        );
        setTableData(result.data);
        setTotal(result.total);
      } catch (e) {
        if (isAbortError(e)) return;
        setError(e instanceof Error ? e.message : "Error loading data");
        setTableData([]);
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [searchText, sortKey, sortDir, startDate, endDate, agentMicrosoftIds, queueMicrosoftIds, page, limit, columnFilters, queuesDataVersion]);

  // Reset page on search / sort / filter change
  React.useEffect(() => {
    setPage(1);
  }, [searchText, sortKey, sortDir, columnFilters]);

  // Reset column filter state when date/agents/queues change, or a poll detects a real
  // agent/queue change (e.g. a rename) — otherwise the filter dropdowns keep serving
  // cached stale names/ids even after the table itself refetches.
  React.useEffect(() => {
    colFilter.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, agentMicrosoftIds, queueMicrosoftIds, queuesDataVersion]);

  // ── Column filter fetch helpers ───────────────────────────────────────────
  const fetchFilterPage = useCallback(
    async (col: string, pageIndex: number, searchValue: string | null, append: boolean) => {
      if (!startDate || !endDate) return;
      colFilter.abortCol(col);
      const ac = new AbortController();
      colFilter.abortRefs.current[col] = ac;

      const otherFilters = buildColumnFiltersPayload(
        Object.fromEntries(Object.entries(columnFilters).filter(([k]) => k !== col))
      );
      try {
        const res = await fetchUserPerformanceColumnFilter(
          {
            startTime: startDate,
            endTime: endDate,
            agentMicrosoftIds: agentMicrosoftIds ?? null,
            queueMicrosoftIds: queueMicrosoftIds ?? null,
            targetColumn: col,
            searchValue,
            pageIndex,
            pageSize: FILTER_PAGE_SIZE,
            agentNameSearchText: searchText.trim() || null,
            columnFilters: otherFilters,
          },
          { signal: ac.signal }
        );
        if (ac.signal.aborted) return;
        colFilter.setEntry(col, (prev) => {
          const base = append ? prev.options : [];
          const merged = [...new Set([...base, ...res.values])];
          return {
            options: merged,
            hasMore: res.hasMore,
            loadedPage: pageIndex,
            loading: false,
            searchQuery: prev.searchQuery,
          };
        });
      } catch (e) {
        if (ac.signal.aborted) return;
        if (isAbortError(e)) return;
        colFilter.setEntry(col, { loading: false });
      }
    },
    [startDate, endDate, agentMicrosoftIds, queueMicrosoftIds, searchText, columnFilters, colFilter]
  );

  const handleFilterOpen = useCallback(
    (col: string) => {
      const entry = colFilter.getEntry(col);
      if (entry.loadedPage >= 0 && entry.searchQuery === "") return;
      colFilter.setEntry(col, { loading: true, searchQuery: "", loadedPage: -1, options: [], hasMore: false });
      fetchFilterPage(col, 0, null, false);
    },
    [colFilter, fetchFilterPage]
  );

  const handleFilterClose = useCallback(
    (col: string) => {
      const timer = colFilter.searchTimers.current[col];
      if (timer) { clearTimeout(timer); delete colFilter.searchTimers.current[col]; }
    },
    [colFilter]
  );

  const handleFilterSearchChange = useCallback(
    (col: string, query: string) => {
      colFilter.setEntry(col, { searchQuery: query });
      const prev = colFilter.searchTimers.current[col];
      if (prev) clearTimeout(prev);
      colFilter.searchTimers.current[col] = setTimeout(() => {
        colFilter.setEntry(col, { loading: true, loadedPage: -1, options: [], hasMore: false });
        fetchFilterPage(col, 0, query.trim() || null, false);
      }, 400);
    },
    [colFilter, fetchFilterPage]
  );

  const handleFilterLoadMore = useCallback(
    (col: string) => {
      const entry = colFilter.getEntry(col);
      if (!entry.hasMore || entry.loading) return;
      colFilter.setEntry(col, { loading: true });
      const isSearch = entry.searchQuery.trim().length > 0;
      fetchFilterPage(col, entry.loadedPage + 1, isSearch ? entry.searchQuery.trim() : null, true);
    },
    [colFilter, fetchFilterPage]
  );

  const handleToggleFilterValue = useCallback((col: string, val: string) => {
    setColumnFilters((prev) => {
      const cur = prev[col] ?? [];
      const next = cur.includes(val) ? cur.filter((v) => v !== val) : [...cur, val];
      return next.length ? { ...prev, [col]: next } : Object.fromEntries(Object.entries(prev).filter(([k]) => k !== col));
    });
  }, []);

  const handleClearFilter = useCallback((col: string) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      delete next[col];
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((col: string, vals: string[]) => {
    if (vals.length === 0) return;
    setColumnFilters((prev) => ({ ...prev, [col]: vals }));
  }, []);

  // ── Sort ──────────────────────────────────────────────────────────────────
  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  // ── Progress bars ─────────────────────────────────────────────────────────
  const maxValues = useMemo(() => {
    const result: Record<string, number> = {};
    columns.forEach((col) => {
      if (col.key !== "userName" && col.key !== "lastCallTaken") {
        const vals = tableData.map((r) => Number(r[col.key as keyof UserPerformanceTableRow] ?? 0));
        result[col.key] = Math.max(...vals, 0);
      }
    });
    return result;
  }, [tableData]);

  // ── Cell rendering ────────────────────────────────────────────────────────
  const renderUserNameCell = (row: UserPerformanceTableRow) => {
    const name = String(row.userName ?? "");
    return <UserNameCell name={name} onClick={() => setSelectedUser(name)} />;
  };

  const renderLastCallTakenCell = (value: unknown) => {
    const formatted = formatRelativeTime(value as string | null);
    return (
      <span className={cn("text-xs", formatted === "—" ? "text-muted-foreground" : "")}>
        {formatted}
      </span>
    );
  };

  const renderAhtCell = (value: unknown) => {
    const numVal = Number(value ?? 0);
    const maxVal = maxValues.aht || 1;
    const pct = maxVal > 0 ? Math.min((numVal / maxVal) * 100, 100) : 0;
    const barColor = theme === "dark" ? "bg-info" : "bg-action";
    return renderMetricBar(numVal, pct, barColor, "", formatDuration(numVal));
  };

  const renderSlaCell = (value: unknown) => {
    if (value == null) {
      return <span className="text-xs text-muted-foreground">—</span>;
    }
    const numVal = Number(value);
    const maxVal = maxValues.sla || 1;
    const pct = maxVal > 0 ? Math.min((numVal / maxVal) * 100, 100) : 0;
    const { text, bar } = getSlaColors(numVal);
    return renderMetricBar(numVal, pct, bar, text, formatPercentage(numVal));
  };

  const renderTransferredPctCell = (value: unknown) => {
    if (value == null) {
      return <span className="text-xs text-muted-foreground">—</span>;
    }
    const numVal = Number(value);
    const maxVal = maxValues.transferredPct || 1;
    const pct = maxVal > 0 ? Math.min((numVal / maxVal) * 100, 100) : 0;
    const { text, bar } = getTransferredPctColors(numVal);
    return renderMetricBar(numVal, pct, bar, text, formatPercentage(numVal));
  };

  const renderDefaultMetricCell = (key: string, value: unknown) => {
    const numVal = Number(value ?? 0);
    const maxVal = maxValues[key] || 1;
    const pct = maxVal > 0 ? Math.min((numVal / maxVal) * 100, 100) : 0;
    const barColor = theme === "dark" ? "bg-info" : "bg-action";
    return renderMetricBar(numVal, pct, barColor, "", String(numVal));
  };

  const getCellContent = (key: string, row: UserPerformanceTableRow) => {
    const value = row[key as keyof UserPerformanceTableRow];
    switch (key) {
      case "userName":
        return renderUserNameCell(row);
      case "lastCallTaken":
        return renderLastCallTakenCell(value);
      case "aht":
        return renderAhtCell(value);
      case "sla":
        return renderSlaCell(value);
      case "transferredPct":
        return renderTransferredPctCell(value);
      default:
        return renderDefaultMetricCell(key, value);
    }
  };

  const renderTableBody = () => {
    if (loading) {
      return skeletonRowIds.map((rowId) => <TableSkeletonRow key={rowId} columns={columns} />);
    }

    if (error || tableData.length === 0) {
      return (
        <TableEmptyRow colSpan={columns.length}>
          No agent&apos;s data
        </TableEmptyRow>
      );
    }

    return tableData.map((row, idx) => (
      <TableRow key={`${row.userName}-${idx}`} index={idx}>
        {columns.map((col, colIdx) => (
          <TableCell key={col.key} index={idx} sticky={colIdx === 0} style={{ width: col.width }}>
            <div className="text-left truncate w-full text-xs">
              {getCellContent(col.key, row)}
            </div>
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Card className={TABLE_CARD_CLASS}>
      <CardHeader className="flex flex-row items-center justify-between sticky top-0 z-30 border-b">
        <div className="flex items-center gap-2">
          <WidgetTitle>User Performance</WidgetTitle>
          <InfoTooltip content="The list of all users, showing their overview of activities within given time" />
        </div>
        <div className="flex items-center gap-2">
          <SearchInput
            placeholder="Search users..."
            onSearch={(v) => { setSearchText(v); }}
            initialValue={searchText}
            debounceMs={300}
          />
          <Button
            variant="outline"
            // eslint-disable-next-line no-restricted-syntax -- Export button chrome, not table row/header coloring; not part of the shared table style contract.
            className="h-8 text-xs flex items-center gap-1 hover:bg-brand-accent hover:border-[#00FEC1] hover:text-black transition-colors dark:bg-card dark:border-[#4A6B85] dark:text-white dark:hover:bg-brand-accent dark:hover:border-[#00FEC1] dark:hover:text-black"
            onClick={async () => {
              if (startDate && endDate) {
                await exportToExcel(startDate, endDate, agentMicrosoftIds ?? null, queueMicrosoftIds ?? null, sortKey, sortDir, setExportLoading);
              }
            }}
            disabled={!startDate || !endDate || exportLoading}
          >
            {exportLoading ? (
              <>
                <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-3 w-3" />
                Export
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0 flex-1 flex flex-col min-h-0">
        <div className="relative overflow-hidden flex-1 flex flex-col min-h-0">
          <TableScrollContainer scrollY={!loading && tableData.length > 0}>
            <Table>
              <TableHeader>
                <TableHeaderRow>
                  {columns.map((col, colIdx) => (
                    <TableHeaderCell key={col.key} sticky={colIdx === 0} style={{ width: col.width }}>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          data-testid="column-sort-trigger"
                          className="flex items-center gap-1 cursor-pointer"
                          onClick={() => handleSort(col.key)}
                        >
                          <span className={cn("whitespace-nowrap truncate text-xs", TABLE_HEADER_TITLE_CLASS)}>
                            {col.title}
                          </span>
                          {sortKey === col.key && (
                            sortDir === "asc" ? <ChevronUp className="h-3 w-3 flex-shrink-0" /> : <ChevronDown className="h-3 w-3 flex-shrink-0" />
                          )}
                        </button>
                        {col.filterable && (
                          <ColFilterPopover
                            columnKey={col.key}
                            selectedValues={columnFilters[col.key] ?? []}
                            entry={colFilter.getEntry(col.key)}
                            onOpen={() => handleFilterOpen(col.key)}
                            onClose={() => handleFilterClose(col.key)}
                            onToggleValue={handleToggleFilterValue}
                            onSearchChange={handleFilterSearchChange}
                            onLoadMore={handleFilterLoadMore}
                            onClearFilter={handleClearFilter}
                            onSelectAll={handleSelectAll}
                          />
                        )}
                      </div>
                    </TableHeaderCell>
                  ))}
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {renderTableBody()}
              </TableBody>
            </Table>
          </TableScrollContainer>
        </div>
        <TablePagingFooter
          currentPage={page}
          totalPages={Math.max(1, Math.ceil(total / limit))}
          total={total}
          pageSize={limit}
          onPageChange={setPage}
          isLoading={loading}
          pageSizeOptions={[5, 10, 20, 50]}
          onPageSizeChange={(size) => { setLimit(size); setPage(1); }}
        />
      </CardContent>

      <UserAgentDetailDialog
        open={selectedUser !== null}
        onOpenChange={(open) => { if (!open) setSelectedUser(null); }}
        userName={selectedUser}
        agentId={selectedAgentId}
        startDate={startDate}
        endDate={endDate}
      />
    </Card>
  );
};

export default UserPerformanceTableWidget;
